import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { successToast } from '@components/toasts/Toasts';
import { SimpleBar } from '@shared/components/simpleBar/simpleBar';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import type { RestaurantEvent } from '@shared/types/restaurants';
import { buildEventTicketPurchasePath } from '@shared/utils/eventUrls';
import { getWebCustomerHost } from '@shared/utils/getWebCustomerHost';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import typography from '~styles/typography.scss';
import { getRestaurantEvent } from './apiHelpers';
import { EventFormStepIndicators } from './EventFormStepIndicators';
import {
  EVENT_STEPS,
  getDefaultEventFormValues,
  getFirstEventFormStepIdWithErrors,
  RestaurantEventForm,
  type EventFormData,
} from './form/RestaurantEventForm';
import {
  useCreateRestaurantEvent,
  useDeleteRestaurantEvent,
  useEventFormPreview,
  useFormStepScroll,
  useUpdateRestaurantEvent,
} from './hooks';
import { RestaurantEventCardPreview } from './RestaurantEventCardPreview';
import styles from './RestaurantEventManagementModal.scss';

export type PendingStepData = Partial<RestaurantEvent>;

export const RestaurantEventManagementModal = ({
  isOpen,
  onClose,
  selectedRestaurantEventId,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedRestaurantEventId: string | null;
}) => {
  const restaurant = useRestaurant();

  const { data: fetchedEvent } = useQuery<RestaurantEvent>({
    enabled: !!selectedRestaurantEventId,
    queryKey: [
      'restaurants',
      restaurant.id,
      'restaurantEvents',
      selectedRestaurantEventId,
    ],
    queryFn: () => {
      if (!selectedRestaurantEventId) {
        throw new Error('Event ID is required');
      }
      return getRestaurantEvent(restaurant.id, selectedRestaurantEventId);
    },
  });

  const {
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<EventFormData>({
    defaultValues: getDefaultEventFormValues(fetchedEvent ?? null, restaurant),
  });

  const {
    isOpen: isWarningModalOpen,
    data: warnings,
    open: openWarningModal,
    close: closeWarningModal,
  } = useIsOpenWithData<string[]>();

  const pendingUpdateRef = useRef<EventFormData | null>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const createRestaurantEvent = useCreateRestaurantEvent(handleClose);
  const updateRestaurantEvent = useUpdateRestaurantEvent({
    restaurantEvent: fetchedEvent ?? undefined,
    onSuccess: handleClose,
    onWarnings: openWarningModal,
  });
  const deleteRestaurantEvent = useDeleteRestaurantEvent(handleClose);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleDeleteClick = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleDeleteConfirmClose = useCallback(() => {
    setIsDeleteConfirmOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (fetchedEvent?.id) {
      deleteRestaurantEvent.mutate(fetchedEvent.id);
      handleDeleteConfirmClose();
    }
  }, [fetchedEvent?.id, deleteRestaurantEvent, handleDeleteConfirmClose]);

  const handleCopyEventUrl = useCallback(async () => {
    if (!fetchedEvent) return;
    const url = `${getWebCustomerHost()}${buildEventTicketPurchasePath(restaurant, fetchedEvent)}`;
    await navigator.clipboard.writeText(url);
    successToast({ message: 'Event URL copied to clipboard' });
  }, [fetchedEvent, restaurant]);

  const formSubmissionInProgress =
    isSubmitting ||
    createRestaurantEvent.isPending ||
    updateRestaurantEvent.isPending ||
    deleteRestaurantEvent.isPending;

  const onSubmit = useCallback(
    (formValues: EventFormData) => {
      if (fetchedEvent) {
        pendingUpdateRef.current = formValues;
        updateRestaurantEvent.mutate({ ...formValues, ignoreWarnings: false });
      } else {
        createRestaurantEvent.mutate(formValues);
      }
    },
    [fetchedEvent, createRestaurantEvent, updateRestaurantEvent],
  );

  const onConfirmUpdateWithWarnings = useCallback(() => {
    const pending = pendingUpdateRef.current;
    closeWarningModal();
    if (pending) {
      pendingUpdateRef.current = null;
      updateRestaurantEvent.mutate({ ...pending, ignoreWarnings: true });
    }
  }, [closeWarningModal, updateRestaurantEvent]);

  useEffect(() => {
    if (!isOpen) return;
    reset(getDefaultEventFormValues(fetchedEvent ?? null, restaurant));
  }, [isOpen, fetchedEvent, restaurant, reset]);

  const formValues = watch();
  const eventOverrides = useEventFormPreview(
    formValues,
    fetchedEvent ?? null,
    restaurant,
  );

  const stepIds = useMemo(() => EVENT_STEPS.map((step) => step.id), []);
  const { setScrollContainerRef, setSectionRef, activeStepId, scrollToStep } =
    useFormStepScroll(stepIds, isOpen);

  const onSubmitInvalid = useCallback(
    (errors: FieldErrors<EventFormData>) => {
      const stepId = getFirstEventFormStepIdWithErrors(errors);
      if (stepId) {
        scrollToStep(stepId);
      }
    },
    [scrollToStep],
  );

  const handleNext = () => {
    const nextStepIndex =
      EVENT_STEPS.findIndex((step) => step.id === activeStepId) + 1;
    const nextStep = EVENT_STEPS[nextStepIndex];

    if (!nextStep) return;
    scrollToStep(nextStep.id);
  };

  const disableNextButton = !(
    EVENT_STEPS.findIndex((step) => step.id === activeStepId) <
    EVENT_STEPS.length - 1
  );

  const modalType = selectedRestaurantEventId ? 'Edit Event' : 'Create Event';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className={styles.modal}
      title={modalType}
      titleClassName={styles.modalTitle}
    >
      <div className={styles.modalInner}>
        <div className={styles.modalContent}>
          <div className={styles.modalContentLeft}>
            <SimpleBar className={styles.simpleBar}>
              <RestaurantEventCardPreview
                fetchedEvent={fetchedEvent ?? null}
                eventOverrides={eventOverrides}
              />
            </SimpleBar>
          </div>
          <div className={styles.modalContentRight}>
            <div className={styles.scrollWrapper}>
              <section
                className={styles.scrollWrapperInner}
                ref={setScrollContainerRef}
                aria-label="Event information form"
              >
                <RestaurantEventForm
                  control={control}
                  setSectionRef={setSectionRef}
                  restaurantEvent={fetchedEvent}
                  getValues={getValues}
                  setValue={setValue}
                  watch={watch}
                  handleSubmit={handleSubmit}
                  onSubmit={onSubmit}
                  onCopyEventUrl={fetchedEvent ? handleCopyEventUrl : undefined}
                />
              </section>
            </div>

            <div className={styles.footer}>
              <div className={styles.footerLeft}>
                <Button
                  label={fetchedEvent ? 'Save & Exit' : 'Create Event'}
                  variant={ButtonVariants.Tertiary}
                  isDisabled={formSubmissionInProgress}
                  onClick={handleSubmit(onSubmit, onSubmitInvalid)}
                />
                {fetchedEvent && (
                  <Button
                    label="Delete Event"
                    variant={ButtonVariants.Error}
                    isDisabled={formSubmissionInProgress}
                    onClick={handleDeleteClick}
                  />
                )}
              </div>
              <Button
                className={styles.footerNext}
                isDisabled={disableNextButton}
                label="Next"
                variant={ButtonVariants.Secondary}
                onClick={handleNext}
              />
            </div>

            <EventFormStepIndicators
              steps={EVENT_STEPS}
              activeStepId={activeStepId}
              onStepClick={scrollToStep}
            />
          </div>
        </div>
      </div>
      <Modal
        isOpen={isWarningModalOpen}
        onClose={closeWarningModal}
        title="Warnings"
      >
        <div>
          {warnings?.map((warning) => (
            <p key={warning} className={typography.c2}>
              {warning}
            </p>
          ))}
          <ModalActions>
            <Button
              label="Cancel"
              onClick={closeWarningModal}
              variant={ButtonVariants.Tertiary}
            />
            <Button
              label="Save Anyway"
              onClick={onConfirmUpdateWithWarnings}
              variant={ButtonVariants.Primary}
            />
          </ModalActions>
        </div>
      </Modal>
      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={handleDeleteConfirmClose}
        title="Delete Restaurant Event"
      >
        <div>
          <p className={typography.c2}>
            Are you sure you&apos;d like to delete this event? This may create
            unassigned reservation(s) if the event has not yet occurred.
          </p>
          <ModalActions>
            <Button
              label="Cancel"
              onClick={handleDeleteConfirmClose}
              variant={ButtonVariants.Tertiary}
            />
            <Button
              label="Delete"
              onClick={handleDeleteConfirm}
              variant={ButtonVariants.Primary}
              isDisabled={deleteRestaurantEvent.isPending}
            />
          </ModalActions>
        </div>
      </Modal>
    </Modal>
  );
};
